import React, { useState } from "react";
import { Upload, FileText, X, Loader2, Info } from "lucide-react";
import SampleContractorSheet from "assets/sample_sheets/Sample_Contractors.xlsx";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";

const SUPPORTED_TYPES = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "text/csv",
];

const formatValidationErrors = (details = []) => {
    return details
        .slice(0, 3)
        .map((item) => {
            const rowLabel = item?.row ? `Row ${item.row}` : "Row";
            const fieldErrors = Object.entries(item?.errors || {})
                .map(([field, value]) => {
                    const message = Array.isArray(value) ? value.join(", ") : String(value);
                    return `${field}: ${message}`;
                })
                .join("; ");
            return fieldErrors ? `${rowLabel} - ${fieldErrors}` : rowLabel;
        })
        .join(" | ");
};

const getErrorMessage = (payload) => {
    if (!payload) {
        return "Failed to upload contractor sheet";
    }
    if (Array.isArray(payload?.details) && payload.details.length > 0) {
        const formattedErrors = formatValidationErrors(payload.details);
        if (formattedErrors) {
            const hasMoreRows = payload.details.length > 3;
            return `${payload.error || "Validation errors in Excel data"}: ${formattedErrors}${hasMoreRows ? " ..." : ""
                }`;
        }
    }
    return payload.error || payload.message || "Failed to upload contractor sheet";
};

export default function ImportContractorModal({ onClose, organizationId, onUploadSuccess }) {
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState("");
    const [messageType, setMessageType] = useState("info");
    const [showInfo, setShowInfo] = useState(false);
    const { addAlert } = useAlert();

    const handleFile = (file) => {
        if (!file) {
            return;
        }
        const fileName = file.name?.toLowerCase() || "";
        const hasValidExtension = fileName.endsWith(".xlsx") || fileName.endsWith(".xls") || fileName.endsWith(".csv");
        const hasValidMime = SUPPORTED_TYPES.includes(file.type);

        if (!hasValidExtension && !hasValidMime) {
            const message = "Please upload an Excel file (.xlsx, .xls or .csv)";
            setUploadMessage(message);
            setMessageType("error");
            addAlert(message, "error");
            return;
        }

        setSelectedFile(file);
        setUploadMessage("");
        setMessageType("info");
    };

    const removeSelectedFile = () => {
        setSelectedFile(null);
        const fileInput = document.getElementById("contractor-bulk-upload-file");
        if (fileInput) {
            fileInput.value = "";
        }
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(true);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        setDragActive(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragActive(false);
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            const message = "Please select an Excel sheet first";
            setUploadMessage(message);
            setMessageType("error");
            addAlert(message, "warning");
            return;
        }

        if (!organizationId) {
            const message = "Organization not found";
            setUploadMessage(message);
            setMessageType("error");
            addAlert(message, "error");
            return;
        }

        setIsUploading(true);
        setUploadMessage("");

        try {
            const formData = new FormData();
            formData.append("organization_id", organizationId);
            formData.append("file", selectedFile);

            const response = await fetch(`${BASE_URL}/org/contractors/bulk-upload/`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("access")}`,
                },
                body: formData,
            });

            const result = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(getErrorMessage(result));
            }

            const successMessage = result?.message || `Successfully created ${result?.created_count || 0} contractors`;
            setUploadMessage(successMessage);
            setMessageType("success");
            addAlert(successMessage, "success");

            if (typeof onUploadSuccess === "function") {
                onUploadSuccess();
            }
            setTimeout(() => {
                onClose?.();
            }, 500);
        } catch (error) {
            const message = error?.message || "Failed to upload contractor sheet";
            setUploadMessage(message);
            setMessageType("error");
            addAlert(message, "error");
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="w-full max-w-xl rounded-xl bg-white shadow-xl">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-gray-900">Import From Excel</h2>
                        <button
                            type="button"
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={() => setShowInfo(!showInfo)}
                        >
                            <Info className="h-5 w-5" />
                        </button>
                    </div>
                    <button
                        type="button"
                        className="text-gray-500 hover:text-gray-700"
                        onClick={onClose}
                        disabled={isUploading}
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="px-6 py-5">
                    {showInfo && (
                        <div className="mb-4 rounded-md bg-blue-50 p-4 text-sm text-blue-700">
                            <ul className="list-disc space-y-1 pl-5">
                                <li>First download the sample sheet,</li>
                                <li>Do not change the column names</li>
                                <li>Just put your contractor details row by row</li>
                                <li>Then save the updated excel sheet and upload here</li>
                            </ul>
                        </div>
                    )}
                    {!selectedFile ? (
                        <div
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? "border-sm-primary bg-sm-primary/5" : "border-gray-300"
                                }`}
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            <input
                                id="contractor-bulk-upload-file"
                                type="file"
                                accept=".xlsx,.xls,.csv"
                                className="hidden"
                                onChange={(event) => handleFile(event.target.files?.[0])}
                                disabled={isUploading}
                            />
                            <label
                                htmlFor="contractor-bulk-upload-file"
                                className={`flex flex-col items-center gap-2 ${isUploading ? "cursor-not-allowed opacity-70" : "cursor-pointer"
                                    }`}
                            >
                                <Upload className="h-8 w-8 text-gray-400" />
                                <span className="text-sm text-gray-700">Click to upload or drag and drop</span>
                                <span className="text-xs text-gray-500">Supports .xlsx, .xls and .csv</span>
                            </label>
                        </div>
                    ) : (
                        <div className="border rounded-lg p-4 border-gray-300">
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <FileText className="h-5 w-5 text-sm-primary shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate" title={selectedFile.name}>
                                            {selectedFile.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {(selectedFile.size / 1024).toFixed(1)} KB
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="text-gray-500 hover:text-red-500"
                                    onClick={removeSelectedFile}
                                    disabled={isUploading}
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="mt-4">
                        <a
                            href={SampleContractorSheet}
                            download
                            className="inline-flex items-center text-sm font-medium text-sm-primary hover:underline"
                        >
                            Download Sample
                        </a>
                    </div>

                    {uploadMessage && (
                        <div
                            className={`mt-4 rounded-lg p-3 text-sm ${messageType === "error"
                                ? "bg-red-50 text-red-700"
                                : messageType === "success"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-blue-50 text-blue-700"
                                }`}
                        >
                            {uploadMessage}
                        </div>
                    )}

                    <div className="mt-6 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                            disabled={isUploading}
                        >
                            Cancel
                        </button>
                        {selectedFile && (
                            <button
                                type="button"
                                onClick={handleUpload}
                                className="px-4 py-2 rounded-md bg-sm-primary text-white text-sm hover:bg-sm-primary/90 disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-2"
                                disabled={isUploading}
                            >
                                {isUploading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    "Upload Sheet"
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
